// src/context/CartContext.js
import React, { createContext, useEffect, useState, useContext, useRef, useCallback } from "react";
import API from "../api/apiUser";
import { useAuth } from "../services/AuthService";
import { useNotification } from "./NotificationContext";

export const CartContext = createContext();

const CART_KEY = "temidayo_balogun_cart";

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState([]);
  const { showNotification } = useNotification();
  const { loading: authLoading } = useAuth();

  const hasLoadedRef = useRef(false);
  const [hydrated, setHydrated] = useState(false);
  const cartRef = useRef(cart);

  // Keep cartRef in sync with cart state
  useEffect(() => {
    cartRef.current = cart;
  }, [cart]);

  // --- Step 1: Load cart from localStorage with 7-day expiration ---
  useEffect(() => {
    try {
      const stored = localStorage.getItem(CART_KEY);
      if (stored) {
        const payload = JSON.parse(stored);
        const items = Array.isArray(payload.items) ? payload.items : (Array.isArray(payload) ? payload : []);
        const last_updated = payload.last_updated;

        // 7 days in milliseconds
        const EXPIRATION_TIME = 7 * 24 * 60 * 60 * 1000;
        const now = Date.now();

        if (last_updated && (now - last_updated > EXPIRATION_TIME)) {
          console.log("Cart expired (> 7 days). Clearing...");
          setCart([]);
          localStorage.removeItem(CART_KEY);
        } else {
          setCart(items);
        }
      } else {
        setCart([]);
      }
    } catch (err) {
      console.error("Failed to parse cart:", err);
      setCart([]);
    } finally {
      setHydrated(true);
    }
  }, []);

  // --- Validate cart ---
  const validateCart = useCallback(async (cartItems = []) => {
    if (!Array.isArray(cartItems) || !cartItems.length) return { valid_cart: [], isValid: false };
    try {
      const res = await API.post("/user/shop/validate-cart/", { cart: cartItems });
      const validCart = Array.isArray(res.data?.valid_cart) ? res.data.valid_cart : [];
      return { valid_cart: validCart, isValid: validCart.length > 0 };
    } catch (err) {
      console.error("Cart validation error:", err);
      return { valid_cart: cartItems, isValid: false };
    }
  }, []);

  // --- Step 2: Validate cart on initial load ---
  useEffect(() => {
    let active = true;

    if (authLoading || hasLoadedRef.current || !hydrated || !cart.length) {
      if (hydrated) hasLoadedRef.current = true;
      return;
    }

    const initCart = async () => {
      try {
        const validated = await validateCart(cart);

        if (!active) return; // Prevent resurrection if cart was cleared or changed

        const finalCart = Array.isArray(validated?.valid_cart)
          ? validated.valid_cart.map(validItem => {
            // Ensure we don't lose the local quantity if backend provides it as undefined
            const existingItem = cart.find(i => i.id === validItem.id);
            return {
              ...validItem,
              quantity: validItem.quantity || existingItem?.quantity || 1
            };
          })
          : cart;

        // Final sanity check: if the cart state has already become empty, don't update it
        setCart(current => current.length === 0 ? [] : finalCart);

        // Persist validated cart with updated timestamp
        localStorage.setItem(CART_KEY, JSON.stringify({
          items: finalCart,
          last_updated: Date.now()
        }));

        hasLoadedRef.current = true;
      } catch (err) {
        console.error("Cart init failed:", err);
      }
    };

    initCart();

    return () => {
      active = false;
    };
  }, [authLoading, hydrated, cart, validateCart]);

  // --- Persist cart to localStorage on changes ---
  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(CART_KEY, JSON.stringify({
      items: cart || [],
      last_updated: Date.now()
    }));
  }, [cart, hydrated]);

  // === CRUD OPERATIONS ===
  const addToCart = useCallback((product, qty = 1) => {
    // Normalize cover image URL
    const imageUrl =
      product.image_url ||
      product.cover ||
      (product.image
        ? `/static/music_uploads/${product.slug?.toLowerCase() || ''}/${product.image}`
        : null);

    const normalizedProduct = {
      ...product,
      name: product.name || product.title,
      title: product.title || product.name,
      image_url: imageUrl,
      quantity: qty
    };

    // Use cartRef to check existence synchronously and avoid race conditions with notifications
    const currentItems = cartRef.current || [];
    const exists = currentItems.find((item) => item.id === normalizedProduct.id);

    let notificationType = "success";
    let notificationTitle = "Added to Cart";
    let notificationMessage = `"${normalizedProduct.name}" was added to your cart.`;

    if (exists) {
      if (normalizedProduct.category_type === "Music Content") {
        notificationType = "info";
        notificationTitle = "Already in Cart";
        notificationMessage = `"${normalizedProduct.name}" is already in your cart.`;
      } else {
        // For Merchandise/Physical items
        const newQty = exists.quantity + qty;
        if (product.stock_quantity && newQty > product.stock_quantity) {
          notificationType = "warning";
          notificationTitle = "Stock Limit Reached";
          notificationMessage = `Only ${product.stock_quantity} units available.`;
        } else {
          notificationType = "success";
          notificationTitle = "Cart Updated";
          notificationMessage = `Updated quantity for "${normalizedProduct.name}".`;
        }
      }
    }

    // Update state
    setCart((currentCart) => {
      const alreadyExists = currentCart.find((item) => item.id === normalizedProduct.id);

      if (alreadyExists) {
        if (normalizedProduct.category_type === "Music Content") {
          return currentCart;
        }

        return currentCart.map((item) => {
          if (item.id === normalizedProduct.id) {
            const newQty = item.quantity + qty;
            if (product.stock_quantity && newQty > product.stock_quantity) {
              return { ...item, quantity: product.stock_quantity };
            }
            return { ...item, quantity: newQty };
          }
          return item;
        });
      }

      return [...currentCart, { ...normalizedProduct, quantity: qty }];
    });

    // Run notification with pre-calculated values
    showNotification({
      type: notificationType,
      title: notificationTitle,
      message: notificationMessage,
    });
  }, [showNotification]);

  const removeFromCart = useCallback((id) => {
    let removedItem = null;
    setCart((currentCart) => {
      removedItem = currentCart.find((item) => item.id === id);
      return currentCart.filter((item) => item.id !== id);
    });

    showNotification({
      type: "warning",
      title: "Item Removed",
      message: removedItem
        ? `"${removedItem.name || removedItem.title}" was removed from your cart.`
        : "Item removed.",
    });
  }, [showNotification]);

  const increaseQty = useCallback((id) => {
    let itemName = "Item";
    let newQty = 0;
    setCart((currentCart) => {
      return currentCart.map((item) => {
        if (item.id === id) {
          itemName = item.name || item.title || "Item";
          newQty = item.quantity + 1;
          return { ...item, quantity: newQty };
        }
        return item;
      });
    });

    if (newQty > 0) {
      showNotification({
        type: "info",
        title: "Quantity Increased",
        message: `"${itemName}" quantity increased to ${newQty}.`,
      });
    }
  }, [showNotification]);

  const decreaseQty = useCallback((id) => {
    let removedItem = null;
    let updatedItem = null;

    setCart((currentCart) => {
      let updated = currentCart.map((item) => {
        if (item.id === id) {
          if (item.quantity <= 1) {
            removedItem = item;
            return { ...item, quantity: 0 };
          }
          updatedItem = { ...item, quantity: item.quantity - 1 };
          return updatedItem;
        }
        return item;
      });

      return updated.filter((item) => item.quantity > 0);
    });

    if (removedItem) {
      showNotification({
        type: "warning",
        title: "Item Removed",
        message: `"${removedItem.name || removedItem.title}" was removed from your cart.`,
      });
    } else if (updatedItem) {
      showNotification({
        type: "info",
        title: "Quantity Decreased",
        message: `"${updatedItem.name || updatedItem.title}" quantity decreased to ${updatedItem.quantity}.`,
      });
    }
  }, [showNotification]);

  const clearCart = useCallback(() => {
    console.log("CartContext: clearCart called.");
    setCart([]);
    localStorage.removeItem(CART_KEY);
  }, []);

  return (
    <CartContext.Provider
      value={{
        cart: cart || [],
        hydrated,
        addToCart,
        removeFromCart,
        increaseQty,
        decreaseQty,
        setCart,
        validateCart,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
